import React, { useEffect, useState, useRef, useMemo } from 'react';
import { DiagramComponent, NodeConstraints, DiagramTools } from '@syncfusion/ej2-react-diagrams';
import { DataManager } from "@syncfusion/ej2-data";
import { useParams } from 'react-router-dom';
import {
    Box, Button, TextField, Typography, Drawer,
    Divider, IconButton, Stack, Tooltip, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Pagination, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import FunctionsIcon from '@mui/icons-material/Functions';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import ConnectionTreeView from '../components/connection/ConnectionTreeView';
import { useNavigate } from 'react-router-dom';
import { useAppSnackbar } from '../contexts/AppSnackbarContext';
import { useAppConfirm } from '../contexts/AppConfirmContext';

const CUSTOM_STEPS = [
    {
        id: 'RetrieveDocumentData',
        name: 'Extract Document Data Step',
        stepType: 'Workflow.Steps.Data.ExtractionDocumentDataStep, Workflow',
        isCustomStep: true,
        icon: <FunctionsIcon />
    },
    {
        id: 'CreateTask',
        name: 'Create a new task',
        stepType: 'Workflow.Steps.Tasks.CreateTaskStep, Workflow',
        isCustomStep: true,
        icon: <FunctionsIcon />
    },
]

//helper function to convert to kebabcase
function toKebabCase(str) {
    return str
        .replace(/([a-z])([A-Z])/g, '$1-$2') // Add a hyphen between lowercase and uppercase letters
        .replace(/\s+/g, '-')               // Replace spaces with hyphens
        .toLowerCase();                     // Convert to lowercase
}
function toPascalCase(input) {
    if (input == null) return "";

    // Normalize & strip accents
    let s = String(input)
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "");

    // Expose word boundaries (camelCase, acronyms, digits, apostrophes)
    s = s
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/([A-Za-z])([0-9])/g, "$1 $2")
        .replace(/([0-9])([A-Za-z])/g, "$1 $2")
        .replace(/['�]/g, " ");

    // Keep only word characters and join
    const parts = s.match(/[A-Za-z0-9]+/g) || [];

    let result = parts
        .map((part) => {
            // Preserve 2-letter acronyms (e.g., "IO")
            if (/^[A-Z]{2}$/.test(part)) return part;
            const lower = part.toLowerCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join("");

    // Ensure valid identifier start (prefix "_" if it would start with a digit)
    if (result && !/^[_A-Za-z]/.test(result)) result = "_" + result;

    return result;
}

export default function WorkflowEditor() {
    const treeObj = useRef(null);
    const [mappings, setMappings] = useState({});
    const [rules, setRules] = useState({});
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [originalWf, setOriginalWf] = useState(null);
    const [workflowName, setWorkflowName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { workflowId } = useParams();
    const [page, setPage] = useState(1);
    const [ruleSetPage, setRuleSetPage] = useState(1);
    const currentEvent = useRef(null);
    const [refreshTree, setRefreshTree] = useState(0);

    const handleMappingPageChange = (_, value) => {
        if (value !== page) {
            setPage(value);
            getMappings();
        }
    }

    const handleRuleSetPageChange = (_, value) => {
        if (value !== ruleSetPage) {
            setRuleSetPage(value);
            getRuleSets();
        }
    }

    async function getMappings() {
        try {
            //setLoading(true);
            const response = await fetch(`/api/mapping?page=${page}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setMappings(data);
            }
        } catch (error) {
            console.error('Error fetching mappings:', error);
        } finally {
            //setLoading(false);
        }
    }

    async function getRuleSets() {
        try {
            const res = await fetch(`/api/rules?page=${ruleSetPage}&pageSize=${15}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            setRules(data)
            //setGridData({
            //    result: formatData(data.items),
            //    count: data.total
            //});
        } catch (error) {
            console.error('Error fetching rules:', error);
        } finally {
        }
    }

    useEffect(() => {
        getMappings();
        getRuleSets();
        const newWf = {
            Id: ``,
            Version: 1,
            Steps: [

            ]
        };
        setSelectedWorkflow(newWf);
    }, []);

    useEffect(() => {
        let isMounted = true;
        async function fetchData() {
            // Fetch workflow
            if (workflowId) {
                try {
                    setIsLoading(true);
                    const encodedEventId = encodeURIComponent(workflowId);
                    const res = await fetch(`/api/workflow/${encodedEventId}`, { credentials: 'include' });
                    if (!res.ok) {
                        setIsLoading(false);
                        return;
                    }
                    const data = await res.json();
                    let wf = data.workflow;
                    currentEvent.current = data.connectionEvent;
                    if (wf.definition) {
                        try {
                            let wfObj = typeof wf.definition === 'string'
                                ? JSON.parse(wf.definition)
                                : wf.definition;
                            if (isMounted) {
                                setWorkflowName(wf.name.split('@')[0]);
                                let original = { ...wfObj, Steps: wfObj.Steps.map(st => ({ ...st, Name: st.Id })) };
                                setSelectedWorkflow(original);
                                setOriginalWf(original);
                            }
                        } catch (e) { console.log(e) }
                    }
                } catch (ex) { }
                finally {
                    setIsLoading(false);
                }

            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [workflowId]);

    return (
        <>
            {isLoading &&
                (< Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box >)}
            {!isLoading &&
                (< Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'row', gap: 3 }}>
                    <Box sx={{ width: "30%", borderRight: '1px solid #cccc', height: '100%', p: 2, overflowY: 'auto' }}>
                        <Stack direction="column" gap={2}>
                            <Typography variant="h8">Actions and Events</Typography>
                            <ConnectionTreeView
                                refreshTree={refreshTree}
                                isDraggable={true}
                                reference={treeObj}
                            />
                            <Divider></Divider>
                            <Typography variant="h8">RuleSets</Typography>
                            <List dense={false}>
                                {rules.items && rules.items.map(rule => (
                                    <div
                                        key={rule.id}
                                        draggable={true}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('application/json', JSON.stringify({ ...rule, isRule: true }));
                                        }}
                                        style={{ cursor: 'grab' }}
                                    >
                                        <ListItem>
                                            <ListItemIcon>
                                                <ArticleIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={rule.name}
                                                secondary={null}
                                            />
                                        </ListItem>
                                    </div>
                                ))}
                            </List>
                            {rules.items && rules.total && (
                                <Pagination page={ruleSetPage} count={rules.totalPages} onChange={handleRuleSetPageChange} style={{ margin: '20px auto', textAlign: 'center' }} />
                            )}
                            <Divider></Divider>
                            <Typography variant="h8">Mappings</Typography>
                            <List dense={false}>
                                {mappings.items && mappings.items.map(mp => (
                                    <div
                                        key={mp.id}
                                        draggable={true}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('application/json', JSON.stringify({ ...mp, isMapping: true }));
                                        }}
                                        style={{ cursor: 'grab' }}
                                    >
                                        <ListItem>
                                            <ListItemIcon>
                                                <ArticleIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={mp.name}
                                                secondary={null}
                                            />
                                        </ListItem>
                                    </div>
                                ))}
                            </List>
                            {mappings.items && mappings.totalPages && (
                                <Pagination page={page} count={mappings.totalPages} onChange={handleMappingPageChange} style={{ margin: '20px auto', textAlign: 'center' }} />
                            )}
                            <Divider></Divider>
                            <Typography variant="h8">Custom Steps</Typography>
                            <List dense={false}>
                                {CUSTOM_STEPS.map((cs, idx) => (

                                    <div
                                        key={cs.id}
                                        draggable={true}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('application/json', JSON.stringify(cs));
                                        }}
                                        style={{ cursor: 'grab' }}
                                    >
                                        <ListItem>
                                            <ListItemIcon>
                                                {cs.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={cs.name}
                                                secondary={null}
                                            />
                                        </ListItem>
                                    </div>
                                ))}
                            </List>
                        </Stack>
                    </Box >
                    <Box sx={{ width: '70%', display: 'flex', flexDirection: 'column', p: 2 }}>
                        <div style={{ overflow: 'auto', height: '100vh' }}>

                            <WorkflowDiagramViewer workflow={selectedWorkflow} setWorkflow={setSelectedWorkflow} workflowName={workflowName} setWorkflowName={setWorkflowName} workflowId={workflowId} currentEvent={currentEvent} originalWf={originalWf} setOriginalWf={setOriginalWf} />
                        </div>
                    </Box>



                </Box >)
            }
        </>
    );
}

function WorkflowDiagramViewer({ workflow, setWorkflow, workflowName, setWorkflowName, workflowId, currentEvent, originalWf, setOriginalWf }) {
    //const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [wfEvent, setWfEvent] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    //const dataManager = useRef(null);
    const diagramRef = useRef();
    const navigate = useNavigate();
    const { showSnackbar } = useAppSnackbar();
    const { confirm } = useAppConfirm();
    const [counter, setCounter] = useState(1);
    const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
    // Outcomes logic for branches
    const [pendingOutcomeStep, setPendingOutcomeStep] = useState(null);
    const [pendingParentIdx, setPendingParentIdx] = useState(null);
    const [stepSelectionDialogOpen, setStepSelectionDialogOpen] = useState(false);
    const [pendingStepSelectionIdx, setPendingStepSelectionIdx] = useState(null);


    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedStepId, setSelectedStepId] = useState(null);

    const selectedStep = useMemo(() => {
        const steps = (workflow && workflow.Steps) || [];
        if (selectedStepId == null || selectedStepId < 0 || selectedStepId >= steps.length) return null;
        return steps[selectedStepId] || null;
        //return steps.find(s => s.Id === selectedStepId) || null;
    }, [workflow, selectedStepId]);

    // Local draft for Inputs editing (array of {k, v})
    const [inputsDraft, setInputsDraft] = useState([]);
    // Local draft for Outputs editing (array of {k, v})
    const [outputsDraft, setOutputsDraft] = useState([]);

    function getNodeDefaults(node) {
        node.width = 260;
        node.height = 140;
        node.shape = { type: 'HTML' };
        //node.shape = 'Task';
        //node.style = { fill: '#e3f2fd', strokeColor: '#90caf9' };
        node.annotations = node.annotations || [{ content: node.id }];
        node.constraints = NodeConstraints.InConnect | NodeConstraints.OutConnect;
        return node;
    }

    useEffect(() => {
        if (currentEvent.current) {
            setWfEvent({ ConnectionId: currentEvent.current.connectionId, EventId: currentEvent.current.id, Name: currentEvent.current.name, StepType: 'DynamicWorkflow.Workflows.Steps.TriggerEvent, DynamicWorkflow' });
        }
        if (workflow && workflow.Steps) {
            const { nodes, connectors } = workflowToDiagram(workflow);
            //dataManager.current = new DataManager(nodes);
            setNodes(nodes);
            setConnectors(connectors);
        }
    }, [workflow]);

    useEffect(() => {
        if (selectedStep === null) {
            setInputsDraft([]);
            setOutputsDraft([]);
        }
        else {
            const pairInputs = Object.entries(selectedStep.Inputs || {}).map(([k, v]) => ({ k, v: String(v) }));
            const pairOutputs = Object.entries(selectedStep.Outputs || {}).map(([k, v]) => ({ k, v: String(v) }));
            if (pairInputs.length) {
                setInputsDraft(pairInputs);
            }
            if (pairOutputs.length) {
                setOutputsDraft(pairOutputs);
            }
        }
    }, [selectedStep]);

    function workflowToDiagram(workflow) {
        const nodes = [];
        const connectors = [];
        const stepMap = new Map();
        workflow.Steps.forEach((step, idx) => stepMap.set(step.Id, { ...step, _wfIndex: idx }));

        // Parameters for layout
        const centerX = 400;
        const branchXOffset = 200;
        const stepYGap = 150;

        // Track visited steps to avoid duplicate nodes
        const visited = new Set();

        // Recursive function to layout branches
        function layoutStep(stepId, offsetX, offsetY) {
            if (!stepId || visited.has(stepId)) return;
            visited.add(stepId);

            const stepObj = stepMap.get(stepId);
            if (!stepObj) return;
            const stepIdx = stepObj._wfIndex;

            nodes.push({
                id: stepObj.Id,
                offsetX,
                offsetY,
                width: 200,
                height: 100,
                style: { fill: '#e3f2fd', strokeColor: '#90caf9' },
                constraints: NodeConstraints.InConnect | NodeConstraints.OutConnect | NodeConstraints.Select | NodeConstraints.PointerEvents | NodeConstraints.HideThumbs | NodeConstraints.ReadOnly,
                annotations: [
                    {
                        id: "a1",
                        content: stepObj.Id.substring(0, 20) + (stepObj.Id.length > 20 ? '...' : ''),
                        offset: { x: 0.5, y: 0.5 },
                        horizontalAlignment: "Center",
                        verticalAlignment: "Center",
                    }
                ],
                data: { index: stepIdx }
            });

            if (Array.isArray(stepObj.Outcomes) && stepObj.Outcomes.length > 0) {
                for (const outcomeObj of stepObj.Outcomes) {
                    if (outcomeObj && outcomeObj.NextStepId) {
                        const branchOffsetX = offsetX + (outcomeObj.Outcome === "Success" ? -branchXOffset : branchXOffset);
                        const branchOffsetY = offsetY + stepYGap;
                        connectors.push({
                            id: `${stepObj.Id}_outcome_${outcomeObj.Outcome}_${outcomeObj.NextStepId}`,
                            sourceID: stepObj.Id,
                            targetID: outcomeObj.NextStepId,
                            annotations: [{ content: outcomeObj.Outcome }]
                        });
                        layoutStep(outcomeObj.NextStepId, branchOffsetX, branchOffsetY);
                    }
                }
            } else if (stepObj.NextStepId) {
                connectors.push({
                    id: `${stepObj.Id}_to_${stepObj.NextStepId}`,
                    sourceID: stepObj.Id,
                    targetID: stepObj.NextStepId,
                    annotations: [{ content: '' }]
                });
                layoutStep(stepObj.NextStepId, offsetX, offsetY + stepYGap);
            }
        }

        // Start layout from the first step (if any)
        if (workflow.Steps.length > 0) {
            layoutStep(workflow.Steps[0].Id, centerX, stepYGap);
        }

        return { nodes, connectors };
    }

    // Open/close drawer based on diagram selection
    const handleSelectionChange = (args) => {
        if (workflow.Steps.length === 0) return;
        if (args && args.state !== 'Changed') return; // only react after it changed

        const node = args && args.newValue && args.newValue && args.newValue[0];
        if (node) {
            setSelectedStepId(node.properties.data.index);
            setDrawerOpen(true);
        } else {
            setSelectedStepId(null);
            setDrawerOpen(false);
        }
    };

    // Helpers to update the workflow
    const updateSelectedStep = (patch) => {
        if (selectedStepId == null) return;
        let mutateSteps = [...workflow.Steps];
        if (mutateSteps[selectedStepId]) {
            mutateSteps[selectedStepId] = { ...mutateSteps[selectedStepId], ...patch };
            setWorkflow(prev => ({
                ...prev,
                //Steps: (prev.Steps || []).map(s => (s.Id === selectedStepId ? { ...s, ...patch } : s))
                Steps: mutateSteps
            }));
        }
    };

    //Step inputs/outputs handlers

    const commitInputsDraft = (draft, isOutput) => {
        const obj = {};
        draft.forEach(({ k, v }) => {
            if (k && k.trim()) obj[k.trim()] = v;
        });
        if (isOutput) {
            updateSelectedStep({ Outputs: obj });
        } else {
            updateSelectedStep({ Inputs: obj });
        }
    };

    const addInputRow = () => {
        const draft = [...inputsDraft, { k: '', v: '' }];
        setInputsDraft(draft);
    };

    const removeInputRow = (idx) => {
        const draft = inputsDraft.filter((_, i) => i !== idx);
        setInputsDraft(draft);
        commitInputsDraft(draft);
    };

    const addOutputRow = () => {
        const draft = [...outputsDraft, { k: '', v: '' }];
        setOutputsDraft(draft);
    };

    const removeOutputRow = (idx) => {
        const draft = outputsDraft.filter((_, i) => i !== idx);
        setOutputsDraft(draft);
        commitInputsDraft(draft, true);
    };

    const handleOutcomeSelection = (branch) => {
        if (pendingParentIdx == null || !pendingOutcomeStep) return;
        let steps = [...workflow.Steps, pendingOutcomeStep];
        let parentStep = steps[pendingParentIdx];

        // Find the outcome object for the selected branch
        let outcomeObj = parentStep.Outcomes.find(o => o.Outcome === branch);

        // If no outcome yet, create it and point to the new step
        if (!outcomeObj) {
            parentStep.Outcomes.push({
                Outcome: branch,
                NextStepId: pendingOutcomeStep.Id
            });
        } else {
            // Walk the chain of NextStepId for this branch
            let currentStepId = outcomeObj.NextStepId;
            let lastStepIdx = steps.findIndex(s => s.Id === currentStepId);
            let lastStep = steps[lastStepIdx];

            // Traverse until we reach the end of the branch
            while (lastStep && lastStep.NextStepId) {
                lastStepIdx = steps.findIndex(s => s.Id === lastStep.NextStepId);
                lastStep = steps[lastStepIdx];
            }

            // Link the last step in the branch to the new step
            if (lastStep) {
                lastStep.NextStepId = pendingOutcomeStep.Id;
            }
        }
        setWorkflow(prev => ({
            ...prev,
            Steps: steps
        }));
        setOutcomeDialogOpen(false);
        setPendingOutcomeStep(null);
        setPendingParentIdx(null);
        setCounter(prev => prev + 1);
        setSelectedStepId(steps.length - 1);
        setDrawerOpen(true);
    };

    // Drop handler
    const handleDrop = (e) => {
        e.preventDefault();
        const data = e.dataTransfer.getData('application/json');
        if (!data) return;
        const nodeData = JSON.parse(data);

        // Only allow if not ActionDefiner/EventDefiner or connection
        if (['ActionDefiner', 'EventDefiner', 'Connection'].includes(nodeData.treeViewType)) {
            showSnackbar('You need to drag either an Action, Mapping or Custom Action', 'warning');
            return;
        }

        // Generate a new step for the workflow
        const newStep = {
            Name: '',
            ErrorBehavior: 'Terminate',
            Inputs: {},
            Outputs: {},
            // Optionally, add more fields from nodeData
        };
        if (nodeData.isEvent) {
            setWfEvent({ ConnectionId: nodeData.connectionId, EventId: nodeData.id, Name: nodeData.name, StepType: 'DynamicWorkflow.Workflows.Steps.TriggerEvent, DynamicWorkflow' });
            return;
        }
        if (nodeData.isAction) {
            newStep['Id'] = `ActionStep${counter}`;
            newStep['StepType'] = `${nodeData.method}, ${nodeData.namespace}`;
            newStep['Inputs']['ActionId'] = `"${nodeData.id}"`;
            newStep['Inputs']['ConnectionId'] = `"${nodeData.connectionId}"`;
        }
        if (nodeData.isMapping) {
            newStep['Id'] = `MappingStep${counter}`;
            newStep['StepType'] = `Workflow.Steps.Data.MappingStep, Workflow`;
            newStep['Inputs']['MappingId'] = `"${nodeData.id}"`;
        }
        if (nodeData.isRule) {
            newStep['Id'] = `RuleStep${counter}`;
            newStep['StepType'] = `Workflow.Steps.Data.RulesExecutionStep, Workflow`;
            newStep['Inputs']['RuleSetId'] = `"${nodeData.id}"`;
            newStep['Outcomes'] = [];
        }
        if (nodeData.isCustomStep) {
            newStep['Id'] = `${nodeData.id}${counter}`;
            newStep['StepType'] = nodeData.stepType;
        }
        const steps = (workflow && workflow.Steps) ? workflow.Steps : [];
        //let lastBranch = steps.findLast(st => st.hasOwnProperty('Outcomes'));
        const stepsWithOutcomes = steps
            .map((st, idx) => ({ ...st, idx }))
            .filter(st => Array.isArray(st.Outcomes));

        if (stepsWithOutcomes.length > 0) {
            setPendingOutcomeStep(newStep);
            setPendingStepSelectionIdx(null);
            setStepSelectionDialogOpen(true);
        } else if (steps.length > 0) {
            // Standard NextStepId logic if no Outcomes
            let parentidx = steps.length - 1;
            steps[parentidx]['NextStepId'] = newStep['Id'];
            let newSteps = [...steps, newStep];
            setWorkflow(prev => ({
                ...prev,
                Steps: newSteps
            }));
            setSelectedStepId(newSteps.length - 1);
            setCounter(prev => prev + 1);
            setDrawerOpen(true);
        } else {
            // First step
            let newSteps = [...steps, newStep];
            setWorkflow(prev => ({
                ...prev,
                Steps: newSteps
            }));
            setSelectedStepId(newSteps.length - 1);
            setCounter(prev => prev + 1);
            setDrawerOpen(true);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const performSaveWf = async (event) => {
        if (workflow.Steps.length === 0) {
            showSnackbar('Workflow need to have steps defined', 'warning');
            return;
        }
        if (!wfEvent.EventId) {
            showSnackbar('Event for workflow not defined', 'warning');
            return;
        }

        if (workflowName === '') {
            showSnackbar('Workflow Name not defined', 'warning');
            return;
        }
        if (JSON.stringify(originalWf || {}) === JSON.stringify(workflow)) {
            showSnackbar('No changes to save', 'warning');
            return;
        }
        try {
            setIsLoading(true);
            let definition = { ...workflow, Steps: workflow.Steps.map(st => { let obj = { ...st, Id: st.Name }; delete obj.Name; return obj; }) };
            if (workflowId)
                definition = { ...definition, Version: workflow.Version + 1 };
            const payload = {
                Name: workflowName,
                Version: workflowId ? (workflow.Version + 1) : workflow.Version,
                ConnectionEventId: wfEvent.EventId,
                Definition: JSON.stringify(definition)
            };
            if (workflowId) {
                const confirmed = await confirm({
                    title: 'Changes to Workflow Detected',
                    content: 'A new Workflow Version is going to be created, do you want to proceed?',
                    confirmText: 'Confirm',
                    cancelText: 'Cancel'
                });
                if (confirmed) {
                    postSaveWf(payload);
                }
            } else {
                postSaveWf(payload);
            }
        } catch (e) {
            showSnackbar('An error has ocurred while saving Workflow, please retry later', 'error');
        } finally {
            setIsLoading(false);
        }
    }

    const postSaveWf = async (payload) => {
        if (workflowId)
            payload['WorkFlowId'] = workflowId;
        const res = await fetch('/api/workflow', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload)
        });
        if (res.ok) {
            showSnackbar('Workflow saved successfully', 'success');
            navigate('/workflow/editorlist', { replace: true });
        }
    }

    const performDeleteWf = async (event) => {
        if (workflow.Steps.length === 0) {
            showSnackbar('Workflow already empty', 'warning');
            return;
        }
        if (!workflowId) {
            showSnackbar('Workflow not exist yet', 'warning');
            return;
        }
        const res = await fetch(`/api/workflow/${workflowId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            //body: JSON.stringify(payload)
        });
        if (res.ok) {
            showSnackbar('Workflow deleted successfully', 'success');
            navigate('/workflow/editorlist', { replace: true });
        }
    }

    const handleCancelEdition = async () => {
        if (workflowId) {
            const confirmed = await confirm({
                title: 'Cancel changes',
                content: 'Are you sure you want to Cancel? all changes will be lost',
                confirmText: 'Accept',
                cancelText: 'Cancel'
            });
            if (confirmed) {
                setIsLoading(true);
                fetch(`/api/workflow/${workflowId}`, { credentials: 'include' })
                    .then(resp => resp.json())
                    .then(data => {
                        let wf = data.workflow;
                        currentEvent.current = data.connectionEvent;
                        if (wf.definition) {
                            try {
                                let wfObj = typeof wf.definition === 'string'
                                    ? JSON.parse(wf.definition)
                                    : wf.definition;
                                setWorkflowName(wf.name.split('@')[0]);
                                setWorkflow({ ...wfObj, Steps: wfObj.Steps.map(st => ({ ...st, Name: st.Id })) });
                            } catch (e) { console.log(e) }
                        }
                    })
                    .finally(() => setIsLoading(false));
            }
        } else {
            const confirmed = await confirm({
                title: 'Cancel changes',
                content: 'Are you sure you want to cancel creation of workflow?',
                confirmText: 'Accept',
                cancelText: 'Cancel'
            });
            if (confirmed) {
                navigate('/workflow/editorlist');
            }
        }
    }

    const drawerCloseHandler = () => {
        if (selectedStep === null) return;
        let updatedSteps = [...workflow.Steps];
        if (selectedStep.Name && selectedStep.Name !== '') {
            const oldId = updatedSteps[selectedStepId].Id;
            const newId = selectedStep.Name;

            // Check for duplicate name
            if (updatedSteps.some((s, idx) => s.Id === newId && idx !== selectedStepId)) {
                setInputsDraft([]);
                setOutputsDraft([]);
                setSelectedStepId(null);
                setDrawerOpen(false);
                showSnackbar('The step name already exists. Try assigning another unique name among steps.', 'error');
                return;
            }
            let inputP = formatInputDrafts();
            // Update the step's Id
            updatedSteps[selectedStepId] = { ...updatedSteps[selectedStepId], Id: newId, Inputs: inputP };
            // Update all NextStepId and Outcomes[].NextStepId references
            updatedSteps = updatedSteps.map(step => {
                let updatedStep = { ...step };
                if (updatedStep.NextStepId === oldId) {
                    updatedStep.NextStepId = newId;
                }
                if (Array.isArray(updatedStep.Outcomes)) {
                    updatedStep.Outcomes = updatedStep.Outcomes.map(outcome => ({
                        ...outcome,
                        NextStepId: outcome.NextStepId === oldId ? newId : outcome.NextStepId
                    }));
                }
                return updatedStep;
            });
            setWorkflow(prev => ({
                ...prev,
                Steps: updatedSteps
            }));
        } else {
            let inputP = formatInputDrafts();
            if (inputP.keys.length) {
                updatedSteps[selectedStepId] = { ...updatedSteps[selectedStepId], Inputs: inputP };
                setWorkflow(prev => ({
                    ...prev,
                    Steps: updatedSteps
                }));
            }
        }
        
        //updateSelectedStep({ Inputs: inputP });
        setInputsDraft([]);
        setOutputsDraft([]);
        setSelectedStepId(null)
        setDrawerOpen(false)
    };

    const formatInputDrafts = () => {
        let inputP = {};
        inputsDraft.forEach(({ k, v }) => {
            if (
                isNaN(Number(v)) &&
                !(typeof v === 'string' && v.startsWith('"') && v.endsWith('"'))
            ) {
                inputP[k] = `"${v}"`;
            } else {
                inputP[k] = v;
            }
        });
        return inputP;
    };

    const deleteStepHandler = (stepIndex) => {
        if (!workflow || !Array.isArray(workflow.Steps) || workflow.Steps.length === 0) return;

        if (workflow.Steps.length === 1) {
            setInputsDraft([]);
            setOutputsDraft([]);
            setSelectedStepId(null);
            setDrawerOpen(false);
            showSnackbar('The workflow step cannot be deleted; there must be more than one step to perform deletion.', 'warning');
            return;
        }

        let stepsCopy = [...workflow.Steps];
        const stepToDelete = stepsCopy[selectedStepId];
        const deletedId = stepToDelete.Id;
        const successorId = stepToDelete.NextStepId || null;

        // Remove the step
        stepsCopy.splice(selectedStepId, 1);

        // Update all references to the deleted step
        stepsCopy = stepsCopy.map(step => {
            let updatedStep = { ...step };

            // Update direct NextStepId references
            if (updatedStep.NextStepId === deletedId) {
                if (successorId) {
                    updatedStep.NextStepId = successorId;
                } else {
                    delete updatedStep.NextStepId;
                }
            }

            // Update Outcomes branch references
            if (Array.isArray(updatedStep.Outcomes)) {
                updatedStep.Outcomes = updatedStep.Outcomes
                    .map(outcome => {
                        if (outcome.NextStepId === deletedId) {
                            if (successorId) {
                                return { ...outcome, NextStepId: successorId };
                            }
                            // Remove the outcome if no successor
                            return null;
                        }
                        return outcome;
                    })
                    .filter(Boolean);
            }

            return updatedStep;
        });

        // Remove orphan steps
        const referencedIds = new Set();
        stepsCopy.forEach(step => {
            if (step.NextStepId) referencedIds.add(step.NextStepId);
            if (Array.isArray(step.Outcomes)) {
                step.Outcomes.forEach(outcome => {
                    if (outcome.NextStepId) referencedIds.add(outcome.NextStepId);
                });
            }
        });
        const rootId = stepsCopy[0]?.Id;
        const filteredSteps = stepsCopy.filter((step, idx) =>
            idx === 0 || referencedIds.has(step.Id)
        );

        setWorkflow(prev => ({
            ...prev,
            Steps: filteredSteps
        }));

        setInputsDraft([]);
        setOutputsDraft([]);
        setSelectedStepId(null);
        setDrawerOpen(false);
    };

    const propsToMatch = ['ConnectionId', 'EventId', 'ActionId', 'MappingId', 'RuleSetId'];
    const hasAnyProperties = (obj) => {
        return propsToMatch.some(prop => obj.k === prop);
    }

    return (
        <>
            {workflow && (< Dialog open={stepSelectionDialogOpen} onClose={() => setStepSelectionDialogOpen(false)}>
                <DialogTitle>Select Step with Outcomes</DialogTitle>
                <DialogContent>
                    <Typography>Select which step with Outcomes to append the new step to:</Typography>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {workflow.Steps
                            .map((st, idx) => ({ ...st, idx }))
                            .filter(st => Array.isArray(st.Outcomes))
                            .map((st) => (
                                <Button
                                    key={st.idx}
                                    variant="outlined"
                                    onClick={() => {
                                        setPendingParentIdx(st.idx);
                                        setStepSelectionDialogOpen(false);
                                        setOutcomeDialogOpen(true);
                                    }}
                                >
                                    {st.Id}
                                </Button>
                            ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStepSelectionDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>)}
            <Dialog open={outcomeDialogOpen} onClose={() => setOutcomeDialogOpen(false)}>
                <DialogTitle>Select Outcome Branch</DialogTitle>
                <DialogContent>
                    <Typography>Should this step be added to the Success or Fail branch?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleOutcomeSelection('Success')} color="primary">Success</Button>
                    <Button onClick={() => handleOutcomeSelection('Fail')} color="error">Fail</Button>
                    <Button onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
            <Drawer anchor="right" open={drawerOpen} onClose={drawerCloseHandler} variant="temporary"
                ModalProps={{
                    keepMounted: true,
                    sx: (theme) => ({
                        zIndex: (theme?.zIndex?.tooltip ?? 1500) + 1, // > tooltip/appBar/modal
                    }),
                }}
            >
                <Box sx={{ width: 380, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Step details</Typography>
                        <IconButton aria-label="remove step" onClick={deleteStepHandler}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>

                    {selectedStep === null ? (
                        <Typography variant="body2" color="text.secondary">Select a node to edit its details.</Typography>
                    ) : (
                        <>
                            <TextField
                                label="Name"
                                value={selectedStep.Name || ''}
                                onChange={(e) => updateSelectedStep({ Name: toKebabCase(e.target.value) })}
                                fullWidth
                            />

                            <Divider />

                            <Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2">Inputs</Typography>
                                    <Button startIcon={<AddIcon />} size="small" onClick={addInputRow}>Add</Button>
                                </Stack>
                                <Stack spacing={1.25}>
                                    {inputsDraft.map((row, idx) => {
                                        let hasAnyProp = hasAnyProperties(row);
                                        return (
                                            <React.Fragment key={idx}>
                                                {!hasAnyProp && (< Stack direction="row" spacing={1} alignItems="center">
                                                    <TextField
                                                        size="small"
                                                        label="Key"
                                                        value={row.k}
                                                        onChange={(e) => {
                                                            const draft = [...inputsDraft];
                                                            draft[idx] = { ...draft[idx], k: toPascalCase(e.target.value) };
                                                            setInputsDraft(draft);
                                                            commitInputsDraft(draft);
                                                        }}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label="Value"
                                                        value={row.v}
                                                        onChange={(e) => {
                                                            const draft = [...inputsDraft];
                                                            draft[idx] = { ...draft[idx], v: e.target.value };
                                                            setInputsDraft(draft);
                                                            commitInputsDraft(draft);
                                                        }}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <IconButton aria-label="remove output" onClick={() => removeInputRow(idx)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Stack>)}
                                                {hasAnyProp && (
                                                    <Stack key={`${idx}-fix`} direction="row" spacing={1} alignItems="center">
                                                        {row.k === 'ConnectionId' && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>Connection ID</strong> <code>{row.v}</code>
                                                            </Typography>
                                                        )}
                                                        {row.k === 'MappingId' && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>Mapping ID</strong> <code>{row.v}</code>
                                                            </Typography>
                                                        )}
                                                        {row.k === 'ActionId' && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>Action ID</strong> <code>{row.v}</code>
                                                            </Typography>
                                                        )}
                                                        {row.k === 'RuleSetId' && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>RuleSet ID</strong> <code>{row.v}</code>
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Stack>
                            </Box>

                            <Divider />

                            {
                                selectedStep && selectedStep.StepType && < Typography variant="caption" color="text.secondary">
                                    <strong>Step Type</strong> <code>{selectedStep.StepType}</code>
                                </Typography>
                            }

                        </>
                    )
                    }
                </Box >
            </Drawer >
            {
                wfEvent.EventId && (<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pb: 2, pt: 1, pr: 2 }}>
                    <Typography variant="h8">{wfEvent.Name}</Typography><Typography variant="h8">{`URL: api/Events/${wfEvent.EventId}`}</Typography></Box>)
            }
            {
                workflow && (<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pb: 2, pt: 1, pr: 2 }}>
                    <Typography variant="h8">Workflow Version: {workflow.Version}</Typography></Box>)
            }
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pb: 2, pt: 1 }}>
                <Box display="flex" alignItems="Center">
                    <TextField
                        size="small"
                        label="Workflow Name"
                        value={workflowName}
                        onChange={(e) => {
                            setWorkflowName(toKebabCase(e.target.value));
                        }}
                        sx={{ flex: 1, width: 450 }}
                        variant={workflowId ? 'filled' : 'outlined'}
                        disabled={workflowId}
                    />
                </Box>
                <Box display="flex" gap={1} sx={{ pr: 2 }} flexDirection="row">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={performSaveWf}
                        startIcon={<SaveIcon />}
                        disabled={isLoading}
                    >
                        {workflowId ? 'Update' : 'Create'}
                    </Button>
                    {workflowId && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={performDeleteWf}
                            disabled={isLoading}
                        >
                            Delete
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdition}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
            <div
                style={{ position: 'relative', width: '95%', height: '600px' }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                <Box sx={{ flex: 1, minHeight: 600, border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden' }}>
                    {nodes.length > 0 ? (
                        <DiagramComponent
                            ref={diagramRef}
                            id="diagram"
                            width="100%"
                            height="600px"
                            //nodeTemplate={nodeTemplate.bind(this)}
                            //nodeTemplate={diagramNodeTemplate}
                            //getNodeDefaults={getNodeDefaults}
                            nodes={nodes}
                            connectors={connectors}
                            delayUpdate={true}
                            //mode="SVG"
                            selectionChange={handleSelectionChange}
                            tool={DiagramTools.ZoomPan | DiagramTools.SingleSelect}
                            scrollSettings={{ scrollLimit: 'Infinity' }}
                        //snapSettings={{ constraints: SnapConstraints.All }}
                        //readOnly={true} // Set to true for read-only mode

                        //Configures data source for diagram

                        >
                            {/*<Inject services={[DataBinding, FlowchartLayout]} />*/}
                        </DiagramComponent>
                    ) : (
                        <Typography variant="body1" sx={{ p: 4, textAlign: 'center' }}>
                            Drag and Event first to Initialize a Workflow.
                        </Typography>
                    )}
                </Box>
                {workflow && (< Box mt={2}>
                    <TextField
                        label="Definition (JSON Preview)"
                        value={JSON.stringify(workflow, null, 2)}
                        fullWidth
                        multiline
                        minRows={4}
                        InputProps={{ readOnly: true }}
                    />
                </Box>)}

            </div>
        </>
    );
}

// In your nodeTemplate for ConnectionTreeView
const nodeTemplateTree = (data) => {
    const isDraggable = !['ActionDefiner', 'EventDefiner'].includes(data.treeViewType);
    return (
        <div
            draggable={isDraggable}
            onDragStart={isDraggable ? (e) => {
                e.dataTransfer.setData('application/json', JSON.stringify(data));
            } : undefined}
            style={{ cursor: isDraggable ? 'grab' : 'default' }}
        >
            {data.name}
        </div>
    );
};
